<script setup>
  import Hft_SectionWrapper from '@/components/microsites/HeadingForTomorrow/components/Hft_SectionWrapper.vue'
  import { useWindowSize } from '@vueuse/core'
  import { gsap } from 'gsap'
  import { ScrollTrigger } from 'gsap/ScrollTrigger'
  import { computed, onMounted, ref } from 'vue'
  gsap.registerPlugin(ScrollTrigger)
  const { width } = useWindowSize()
  const isMobile = computed(() => width.value < 1024)
  const activeBlinkingDots = ref(0)
  const maxActiveBlinkingDots = ref(35)

  const container = ref(null)

  function fallingStars() {
    if (isMobile.value) return
    if (activeBlinkingDots.value >= maxActiveBlinkingDots.value) return

    activeBlinkingDots.value++
    const $blinkingDiv = document.createElement('div')
    $blinkingDiv.classList.add('m-headingForT-morning-bgs__blinking-dot')
    container.value.appendChild($blinkingDiv)

    // Random position and rotation
    const startX = Math.random() * window.innerWidth
    $blinkingDiv.style.left = `${startX}px`

    const randomSize = 0.2 + Math.random() * 0.4
    $blinkingDiv.style.width = `${randomSize}vw`
    $blinkingDiv.style.height = `${randomSize}vw`

    // Animation
    setTimeout(() => {
      $blinkingDiv.style.top = `${window.innerHeight + 500}px`
      $blinkingDiv.style.opacity = '1'
    }, 3000)
    // Remove after animation
    setTimeout(() => {
      $blinkingDiv.remove()
      activeBlinkingDots.value--
    }, 13000)
  }

  function showFallingStars() {
    fallingStars()
    setInterval(fallingStars, 600)
  }

  onMounted(() => {
    showFallingStars()
    if (window.innerWidth >= 1024) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: '.m-headingForT-morning',
          start: 'top 30%', // when the top of the element hits 80% of the viewport height
          toggleActions: 'play none none none',
          onEnter: () => {
            setTimeout(() => {
              document
                .querySelectorAll('.m-headingForT-morning-bgs__crystal')
                .forEach((el) => el.classList.add('m-headingForT-morning-bgs__crystal--blinking'))
            }, 3000)
          },
        },
      })

      tl.fromTo('.m-headingForT-morning-bgs__middle', { y: '20%' }, { y: '0', duration: 5, ease: 'power2.out' }, '<')
        .fromTo('.m-headingForT-morning-bgs__back', { scale: 1.25 }, { scale: 1, duration: 4, ease: 'power2.out' }, '<')
        .fromTo(
          '.m-headingForT-morning__titles',
          { scale: 1.15, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1, delay: 0.2, ease: 'power2.out' },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning__video',
          { scale: 1.15, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1, delay: 0.4, ease: 'power2.out' },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning__text-box',
          { scale: 1.15, opacity: 0 },
          { scale: 1, opacity: 1, duration: 1, delay: 0.6, ease: 'power2.out' },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning-bgs__deco-right',
          { x: '100%', scale: 1.8, opacity: 0 },
          { x: '0', scale: 1, opacity: 1, duration: 2.3, delay: 0.3, ease: 'power2.out' },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning-bgs__deco-left',
          { x: '-100%', scale: 1.8, opacity: 0 },
          { x: '0', scale: 1, scale: 1, opacity: 1, duration: 2.3, ease: 'power2.out' },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning-bgs__crystal--1, .m-headingForT-morning-bgs__crystal--2, .m-headingForT-morning-bgs__crystal--3, .m-headingForT-morning-bgs__crystal--4',
          { x: '-600%', rotationX: '-150%', rotationZ: '-250%', scale: 9, opacity: 0 },
          {
            x: '0',
            rotationX: '0',
            rotationZ: '0',
            scale: 1,
            scale: 1,
            delay: 0.5,
            opacity: 1,
            duration: 1.5,
            ease: 'power2.out',
          },
          '<'
        )
        .fromTo(
          '.m-headingForT-morning-bgs__crystal--5, .m-headingForT-morning-bgs__crystal--6, .m-headingForT-morning-bgs__crystal--7, .m-headingForT-morning-bgs__crystal--8',
          { x: '600%', rotationX: '-150%', rotationZ: '-250%', scale: 9, opacity: 0 },
          { x: '0', rotationX: '0', rotationZ: '0', scale: 1, scale: 1, opacity: 1, duration: 2.3, ease: 'power2.out' },
          '<'
        )
    }
  })
</script>

<template>
  <Hft_SectionWrapper class="m-headingForT-morning">
    <div class="m-headingForT-morning__container">
      <div ref="container" class="m-headingForT-morning-bgs">
        <img
          v-for="n in 8"
          :key="n"
          src="@/assets/images/microsites/headingfortomorrow/morning/desktop/crystal.png"
          :class="`m-headingForT-morning-bgs__crystal m-headingForT-morning-bgs__crystal--${n}`"
        />
        <div class="m-headingForT-morning-bgs__blinking-dot"></div>
        <div class="m-headingForT-morning-bgs__bottom"></div>
        <div class="m-headingForT-morning-bgs__middle"></div>
        <div class="m-headingForT-morning-bgs__deco-left"></div>
        <div class="m-headingForT-morning-bgs__deco-right"></div>
        <div class="m-headingForT-morning-bgs__back"></div>
      </div>
      <div class="m-headingForT-morning__titles">
        <div class="m-headingForT__title">Morning <br />Star Banquet</div>
        <div class="m-headingForT__sub-title">Available Now!</div>
      </div>
      <div class="m-headingForT-morning__video">
        <iframe
          width="560"
          height="315"
          src="https://www.youtube.com/embed/n-wdK8xtz0s"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowfullscreen
        ></iframe>
      </div>

      <div class="m-headingForT-morning__text-box">
        <div class="m-headingForT-morning__text">
          Infiltrate the grand banquet of the High Flora and learn more about Gerand Darmoor and his Apostles!
        </div>
      </div>
    </div>
  </Hft_SectionWrapper>
</template>

<style lang="scss" scoped>
  @use '@/assets/scss/microsites/headingfortomorrow/fonts.scss' as *;
  @use '@/assets/scss/microsites/headingfortomorrow/variables.scss' as *;

  .m-headingForT-morning {
    text-align: center;
    position: relative;
    /* background: url('~@/assets/images/microsites/headingfortomorrow/morning/bg.jpg') no-repeat center top / 100% auto; */
    /* padding: 2vw 0 2vw; */
    padding-bottom: 6vw;
  }

  .m-headingForT-morning__container {
    max-width: 100%;
    overflow-x: hidden;
  }

  .m-headingForT-morning-bgs {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;

    :deep(.m-headingForT-morning-bgs__blinking-dot) {
      top: -2vw;
      width: 0.3vw;
      height: 0.3vw;
      background-color: white; /* base color */
      border-radius: 50%;
      animation: m-headingForT-morning-bgs__blinking-ele 1.8s infinite;
      z-index: 1;
      position: absolute;
      transition: top 12s linear, left 12s linear, opacity 0.5s;
      box-shadow: 0 0 2vw rgba(white, 1), 0 0 0.5vw rgba(white, 1), 0 0 1vw rgba(white, 1), 0 0 1vw rgba(white, 1),
        0 0 10vw rgba(white, 1), 0 0 10vw rgba(white, 1);
    }
  }

  @keyframes m-headingForT-morning-bgs__blinking-ele {
    60% {
      opacity: 1;
    }
    90% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  .m-headingForT-morning-bgs__crystal {
    width: 4vw;
    height: auto;
    position: absolute;
    top: 10vw;
    z-index: 3;
  }
  .m-headingForT-morning-bgs__crystal--blinking {
    animation: m-headingForT-morning-bgs__blinking-ele 3.8s infinite;
  }
  .m-headingForT-morning-bgs__crystal--1 {
    left: 31vw;
    top: -0.5vw;
    animation-duration: 3.8s;
  }
  .m-headingForT-morning-bgs__crystal--2 {
    left: 23.5vw;
    top: 4.5vw;
    width: 3vw;
    animation-duration: 4.2s;
  }
  .m-headingForT-morning-bgs__crystal--3 {
    left: 17.7vw;
    top: 23.5vw;
    width: 4vw;
    animation-duration: 4.9s;
  }

  .m-headingForT-morning-bgs__crystal--4 {
    left: 13.9vw;
    top: 14.7vw;
    width: 1.7vw;
    animation-duration: 4.4s;
  }

  .m-headingForT-morning-bgs__crystal--5 {
    right: 11.9vw;
    top: 18.7vw;
    width: 3vw;
    animation-duration: 4.6s;
  }

  .m-headingForT-morning-bgs__crystal--6 {
    right: 22.5vw;
    top: 0.5vw;
    width: 2vw;
    animation-duration: 4.8s;
  }

  .m-headingForT-morning-bgs__crystal--7 {
    right: 22.5vw;
    top: 14.6vw;
    width: 3.2vw;
    animation-duration: 4.1s;
  }

  .m-headingForT-morning-bgs__crystal--8 {
    right: 0.05vw;
    top: 24vw;
    width: 4vw;
    animation-duration: 5s;
  }

  .m-headingForT-morning-bgs__deco-right {
    position: absolute;
    width: 16.8vw;
    height: 20.5vw;
    bottom: 28.8vw;
    right: 0;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/desktop/deco-right.png') no-repeat center
      center / 100% auto;
    z-index: 5;
  }

  .m-headingForT-morning-bgs__deco-left {
    position: absolute;
    width: 17vw;
    height: 10vw;
    bottom: 28vw;
    left: 0;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/desktop/deco-left.png') no-repeat center
      center / 100% auto;
    z-index: 5;
  }
  .m-headingForT-morning-bgs__middle {
    position: absolute;
    width: 100%;
    height: 61vw;
    bottom: 0;
    left: 0;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/desktop/bg-middle.png') no-repeat center
      bottom / 100% auto;
    z-index: 2;
  }

  .m-headingForT-morning-bgs__bottom {
    position: absolute;
    width: 100%;
    height: 11vw;
    bottom: 0;
    left: 0;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/desktop/bg-bottom.png') no-repeat center
      center / 100% auto;
    z-index: 3;
  }

  .m-headingForT-morning-bgs__back {
    position: absolute;
    width: 100%;
    height: 56.6vw;
    bottom: 0;
    left: 0;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/desktop/bg-back.jpg') no-repeat center
      center / 100% auto;
    z-index: 1;
  }
  .m-headingForT-morning__titles {
    width: 29vw;
    margin: 0 auto;
    color: #851530;
    position: relative;
    z-index: 10;
    .m-headingForT__title {
      margin-bottom: 0.6vw;
    }
  }
  .m-headingForT-morning__text-box {
    width: 43vw;
    height: 5vw;
    margin: 0 auto 1.5vw;
    background: url('~@/assets/images/microsites/headingfortomorrow/morning/text-bg.png') no-repeat center center / 100%
      auto;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 10;
  }
  .m-headingForT-morning__text {
    width: 70%;
    margin-bottom: 0.2vw;
    color: #e3cd8a;
  }

  .m-headingForT-morning__video {
    border: 5px solid #851530;
    display: inline-block;
    position: relative;
    z-index: 10;
    width: 44vw;
    height: 25vw;
    margin: 1.3vw 0 1vw;
    overflow: hidden;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.2), 0 0 0.6vw rgba(255, 255, 255, 0.4);
    iframe {
      border: 1px solid #e3cd8a;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .tablet {
    .m-headingForT-morning {
      background: url('~@/assets/images/microsites/headingfortomorrow/morning/bg-tablet.jpg') no-repeat center top /
        100% auto;
      padding-bottom: 13.7vw;
    }

    .m-headingForT-morning-bgs {
      display: none;
    }
    .m-headingForT-morning__titles {
      width: 60vw;
    }

    .m-headingForT-morning__video {
      width: 89vw;
      height: 51vw;
      margin: 2.3vw 0 2.3vw;
    }
    .m-headingForT-morning__text-box {
      width: 83vw;
      height: 10vw;
    }
  }
  .mobile {
    .m-headingForT-morning {
      background: url('~@/assets/images/microsites/headingfortomorrow/morning/bg-mobile.jpg') no-repeat center top /
        100% auto;
      padding-bottom: 22vw;
    }
    .m-headingForT-morning-bgs {
      display: none;
    }
    .m-headingForT-morning__titles {
      width: 71vw;
    }
    .m-headingForT-morning__video {
      width: 91vw;
      height: 52vw;
      border: 2px solid #851530;
      margin: 10vw 0 8vw;
    }
    .m-headingForT-morning__text-box {
      width: 100%;
      height: 25vw;
      background: url('~@/assets/images/microsites/headingfortomorrow/morning/text-bg-mobile.png') no-repeat center
        center / 110% auto;
    }
    .m-headingForT-morning__text {
      width: 90%;
      margin-bottom: 1vw;
    }
  }
</style>
